/**
 * DirectoryConfigStep - Wizard step 2 for directory path configuration
 *
 * Collects:
 * - Directory path (required)
 * - Validates path exists and is accessible
 * - Shows estimated file count
 */

import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  FolderOpen,
  Search,
  FileText,
  HardDrive,
} from 'lucide-react';
import { useMonitorCreationStore } from '@/features/monitors/store/monitorCreationStore';
import { useValidateDirectory } from '@/features/monitors/hooks/useMonitors';

/**
 * Format file size from bytes
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * DirectoryConfigStep Component
 *
 * Second step of monitor creation wizard for directory path configuration.
 *
 * @example
 * ```tsx
 * <DirectoryConfigStep />
 * ```
 */
export const DirectoryConfigStep: React.FC = () => {
  const store = useMonitorCreationStore();
  const validateDirectory = useValidateDirectory();

  const [validationResult, setValidationResult] = useState<any>(null);
  const [showValidationError, setShowValidationError] = useState(false);

  /**
   * Handle validate button click
   */
  const handleValidate = async () => {
    if (!store.directory_path) return;

    setShowValidationError(false);
    setValidationResult(null);

    try {
      const result = await validateDirectory.mutateAsync(store.directory_path);
      setValidationResult(result);

      if (result.valid) {
        store.setDirectoryValidated(true);
      } else {
        store.setDirectoryValidated(false);
        setShowValidationError(true);
      }
    } catch (error: any) {
      console.error('Directory validation failed:', error);
      store.setDirectoryValidated(false);
      setShowValidationError(true);
      setValidationResult({
        valid: false,
        exists: false,
        accessible: false,
        errors: [
          {
            field: 'directory_path',
            message: error.response?.data?.detail || 'Failed to validate directory',
            code: 'VALIDATION_ERROR',
          },
        ],
      });
    }
  };

  /**
   * Handle path change
   */
  const handlePathChange = (value: string) => {
    store.setDirectoryPath(value);
    store.setDirectoryValidated(false);
    setValidationResult(null);
    setShowValidationError(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Directory Path</CardTitle>
          <CardDescription>
            Specify the directory path to monitor for new files
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Directory Path Input */}
          <div className="space-y-2">
            <Label htmlFor="directory-path">
              Directory Path <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="directory-path"
                value={store.directory_path}
                onChange={(e) => handlePathChange(e.target.value)}
                placeholder="/data/incoming"
                className={`flex-1 font-mono ${
                  showValidationError ? 'border-destructive' : ''
                }`}
              />
              <Button
                onClick={handleValidate}
                disabled={!store.directory_path || validateDirectory.isPending}
                variant="outline"
              >
                {validateDirectory.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Validate
                  </>
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Provide the absolute path to the directory you want to monitor
            </p>
          </div>

          {/* Validation Results */}
          {validationResult && (
            <div className="space-y-4 mt-4">
              {validationResult.valid ? (
                <Alert variant="success">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Directory validated successfully!</strong>
                    <p className="mt-2">The directory exists and is accessible.</p>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Validation failed:</strong>
                    <ul className="list-disc list-inside mt-2">
                      {validationResult.errors?.map((error: any, index: number) => (
                        <li key={index}>{error.message}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Directory Metadata */}
              {validationResult.valid && validationResult.metadata && (
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" />
                      Directory Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-1 text-info">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Matching Files</p>
                        <p className="text-lg font-semibold">
                          {validationResult.metadata.matching_files !== undefined
                            ? validationResult.metadata.matching_files.toLocaleString()
                            : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="mt-1 text-violet">
                        <HardDrive className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Size</p>
                        <p className="text-lg font-semibold">
                          {validationResult.metadata.total_size !== undefined
                            ? formatFileSize(validationResult.metadata.total_size)
                            : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="mt-1 text-success">
                        <CheckCircle className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Type</p>
                        <p className="text-lg font-semibold">
                          {validationResult.metadata.is_directory ? 'Directory' : 'File'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Alert variant="info">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-medium mb-1">Directory Path Requirements</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Path must be an absolute path (not relative)</li>
            <li>Directory must exist and be accessible</li>
            <li>Read permissions are required for the directory</li>
            <li>Subdirectories can be monitored if recursive option is enabled</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Validation Reminder */}
      {!store.directoryValidated && store.directory_path && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please click "Validate" to verify the directory path before continuing.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
